'use client';

import { useCallback, useEffect, useState } from 'react';

import { getPets } from '@libs/api-proxy-client';
import { logSentryError, SentryAcpAboutYouErrors as SentryErrors, useUserSession } from '@libs/common-next';
import { isMemorialProfilePetStatus } from '@libs/core';

import { getProfileCelebrationUserScope } from './celebration-user-scope';
import {
    hasProfileCompleteNoPetSelected,
    hasProfileCompleteNotificationsSubmitted,
    syncCompleteProfileFlowSessionUser,
} from './complete-profile-flow-session';
import type { DerivedProfileCompletion } from './derive-profile-completion-steps';
import { deriveProfileCompletionSteps } from './derive-profile-completion-steps';
import {
    PROFILE_COMPLETION_PETS_CHANGED_EVENT,
    PROFILE_COMPLETION_SESSION_CHANGED_EVENT,
} from './profile-completion-events';

type UseProfileCompletionStepsOptions = {
    enabled?: boolean;
};

type UseProfileCompletionStepsResult = DerivedProfileCompletion & {
    isLoading: boolean;
    error: Error | null;
    petCount: number;
    refresh: () => Promise<void>;
};

function readSessionFlags() {
    return {
        noPetSelected: hasProfileCompleteNoPetSelected(),
        notificationsSubmitted: hasProfileCompleteNotificationsSubmitted(),
    };
}

export function useProfileCompletionSteps({
    enabled = true,
}: UseProfileCompletionStepsOptions = {}): UseProfileCompletionStepsResult {
    const { user, userData } = useUserSession();
    const userScope = getProfileCelebrationUserScope(user);
    const [petCount, setPetCount] = useState(0);
    const [arePetsLoading, setArePetsLoading] = useState(enabled);
    const [error, setError] = useState<Error | null>(null);
    const [sessionFlags, setSessionFlags] = useState(() => {
        syncCompleteProfileFlowSessionUser(userScope);
        return readSessionFlags();
    });
    const isSessionLoading = enabled && user != null && userData == null;
    const isLoading = arePetsLoading || isSessionLoading;

    const loadPets = useCallback(async () => {
        if (!enabled) {
            setArePetsLoading(false);
            return;
        }

        setArePetsLoading(true);
        setError(null);

        try {
            const pets = await getPets();
            if (!Array.isArray(pets)) {
                throw new TypeError('Failed to load profile completion stepper banner.');
            }
            const activePetCount = pets.filter(pet => !isMemorialProfilePetStatus(pet.petStatus)).length;
            setPetCount(activePetCount);
        } catch (err) {
            const loadError =
                err instanceof Error ? err : new Error('Failed to load profile completion stepper banner.');
            setError(loadError);
            logSentryError(loadError, SentryErrors.CompleteProfileBanner.LoadFailed);
        } finally {
            setArePetsLoading(false);
        }
    }, [enabled]);

    useEffect(() => {
        syncCompleteProfileFlowSessionUser(userScope);
        setSessionFlags(readSessionFlags());
    }, [userScope]);

    useEffect(() => {
        void loadPets();
    }, [loadPets]);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        const handleRefresh = () => {
            setSessionFlags(readSessionFlags());
            void loadPets();
        };

        globalThis.addEventListener(PROFILE_COMPLETION_PETS_CHANGED_EVENT, handleRefresh);
        globalThis.addEventListener(PROFILE_COMPLETION_SESSION_CHANGED_EVENT, handleRefresh);
        return () => {
            globalThis.removeEventListener(PROFILE_COMPLETION_PETS_CHANGED_EVENT, handleRefresh);
            globalThis.removeEventListener(PROFILE_COMPLETION_SESSION_CHANGED_EVENT, handleRefresh);
        };
    }, [enabled, loadPets]);

    const derived = deriveProfileCompletionSteps({
        profile: userData?.profile,
        addresses: userData?.addresses,
        petCount,
        sessionFlags,
    });

    return {
        ...derived,
        isLoading,
        error,
        petCount,
        refresh: loadPets,
    };
}
